import {createSelector} from 'reselect';

import {SEGMENT_FOR_MENTION_ME} from 'constants/analytics/mentionMe';
import {ParamsType} from 'desktop/components/mentionMe/mentionMeTypes';
import {getSuccessPageState} from 'desktop/pages/success/store/selectors/selectors';
import {SuccessPageReducerType} from 'desktop/pages/success/types/types';
import {ApplicationStoreType} from 'desktop/store';
import {getRouterInfoFromWindow} from 'libs/routerInfo/routerInfo';
import {localStore} from 'libs/storage/storage';

/**
 * Will refund the value of the item after deducting tax
 * @return {string} the result of calculating the price of goods after tax deduction
 */
export const getOrderValueAfterTax = createSelector(
    getSuccessPageState,
    (paymentData: SuccessPageReducerType): string => {
        if (paymentData?.orderLast) {
            const {
                orderLast: {grandTotal = 0, shippingAmount = 0, taxAmount: tax = 0},
            } = paymentData;
            const formatTax = typeof tax === 'number' ? tax.toFixed(4) : '0.0000';
            const revenue = Number(grandTotal) - Number(shippingAmount) - Number(formatTax);
            return Math.max(revenue, 0).toFixed(2);
        }

        return '0.00';
    },
);

/**
 * Returns the order id
 * @param {Function} state - redux store
 * @return {ParamsType} object params
 */
export const getOrderIdAndSegment = createSelector(
    [getSuccessPageState],
    (successData: SuccessPageReducerType) => {
        const segmentFromLocalStorage = localStore.get(SEGMENT_FOR_MENTION_ME, '');
        if (getRouterInfoFromWindow()?.action !== 'success')
            return {
                order_number: '',
                segment: '',
            };

        return {
            order_number: successData ? successData.order.incrementId : '',
            segment: segmentFromLocalStorage,
        };
    },
);

/**
 * Returns a order data object to be serialized in a request for MentionMe
 * @return {ParamsType} object params
 */
export const getOrderDataForRequestForMentionMe = createSelector(
    [getOrderIdAndSegment, getOrderValueAfterTax],
    (orderIdSegmentData: ParamsType, orderTotal: string) => ({
        ...orderIdSegmentData,
        order_currency: 'USD',
        order_total: String(orderTotal),
    }),
);

/**
 * Get current shipping address
 */
export const getCurrentShippingAddress = (store: ApplicationStoreType) =>
    // eslint-disable-next-line no-underscore-dangle
    store._checkoutSideReview?.deliveryStep?.shippingAddress;
